/* import __COLOCATED_TEMPLATE__ from './monthly-to-annual-checkout.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type TaskGenerator } from 'ember-concurrency';
import { keepLatestTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type { AfterPaymentMethodSuccess } from 'embercom/components/stripe/stripe-component';
import { INTENT_TYPES } from 'embercom/components/stripe/stripe-component';
import { post } from 'embercom/lib/ajax';
import {
  PERFORMANCE_PRO_BASE_ID,
  BILLING_PERIODS,
  FIN_AI_COPILOT_BASE_ID,
  PRICING_5_X_CORE_ADVANCED_ID,
  PRICING_5_X_CORE_ESSENTIAL_ID,
  PROACTIVE_SUPPORT_PLUS_BASE_ID,
} from 'embercom/lib/billing';
import { PricingModelIdentifier } from 'embercom/lib/billing/pricing-models';
import type CardlessTrialService from 'embercom/services/cardless-trial-service';
import { DEFAULT_REQUEST_PARAMS } from 'embercom/services/quote-service';

import type Router from '@ember/routing/router-service';
import { Metric } from 'embercom/models/data/pricing/metric-types';
import type Plan from 'embercom/models/plan';
import type QuoteService from 'embercom/services/quote-service';
import moment from 'moment-timezone';

interface Args {
  planId: number;
  returnToLastPage?: string | null;
}
interface Signature {
  Element: any;
  Args: Args;
}

export default class MonthlyToAnnualCheckout extends Component<Signature> {
  @service declare appService: AppService;
  @service declare cardlessTrialService: CardlessTrialService;
  @service declare quoteService: QuoteService;
  @service declare customerService: any;
  @service declare intl: any;
  @service declare notificationsService: any;
  @service declare router: Router;
  @service declare store: any;

  @service declare purchaseAnalyticsService: any;

  @tracked billingPeriod = BILLING_PERIODS.Annual;
  @tracked selectedSeatNumber = 1;
  @tracked seatNumber = 1;
  @tracked minimumSeatNumber = 1;
  @tracked selectedProactiveAddon = false;
  @tracked copilotInitialised = false;
  @tracked selectedCopilotAdmins: string[] = [];
  @tracked hasSelectedPerformanceProAddon = false;
  @tracked showCreditCardPage = false;
  @tracked paymentMethod = '';
  @tracked selectedPlanId = '';
  @tracked planId? = Number(this.args.planId);
  @tracked openCopilotModalOnLoad = false;

  INTENT_TYPES = INTENT_TYPES;

  constructor(owner: unknown, args: any) {
    super(owner, args);

    this.initializeFromExistingSubscriptionState();
    this.applyPlanIdParameter();
    this.setSeatsToHumanAdminsOrCoreSeatLength();
    taskFor(this.getQuotes).perform();
  }

  private initializeFromExistingSubscriptionState() {
    this.selectedPlanId = this.customerService.activePricing5CorePlanId
      ? String(this.customerService.activePricing5CorePlanId)
      : PRICING_5_X_CORE_ADVANCED_ID;
    this.initializeCopilotAdmins();
    this.selectedProactiveAddon = this.customerService.billableCustomerPlanIds.includes(
      Number(PROACTIVE_SUPPORT_PLUS_BASE_ID),
    );
    this.hasSelectedPerformanceProAddon = this.customerService.billableCustomerPlanIds.includes(
      Number(PERFORMANCE_PRO_BASE_ID),
    );
  }

  private initializeCopilotAdmins() {
    if (this.adminIdsWithCopilotSeats.length) {
      this.selectedCopilotAdmins = this.adminIdsWithCopilotSeats;
      this.copilotInitialised = true;
    }
  }

  private applyPlanIdParameter() {
    if (!this.planId) {
      return;
    }

    if (this.suitableCorePlanExistsForPlanId(this.planId)) {
      this.selectedPlanId = String(this.planId);
    } else if (this.planId === Number(PROACTIVE_SUPPORT_PLUS_BASE_ID)) {
      this.selectedProactiveAddon = true;
    } else if (this.planId === Number(PERFORMANCE_PRO_BASE_ID)) {
      this.hasSelectedPerformanceProAddon = true;
    } else if (
      this.planId === Number(FIN_AI_COPILOT_BASE_ID) &&
      !this.appService.app.humanAdminsWithCopilotSeat.length
    ) {
      this.openCopilotModalOnLoad = true;
    }
  }

  private setSeatsToHumanAdminsOrCoreSeatLength() {
    let previousMinimum = this.minimumSeatNumber;
    let hasAssignedCoreSeats = this.appService.app.humanAdminsWithCoreSeat.length;
    let viewingEssential = this.selectedPlanId === PRICING_5_X_CORE_ESSENTIAL_ID;

    this.minimumSeatNumber =
      hasAssignedCoreSeats && !viewingEssential
        ? this.appService.app.humanAdminsWithCoreSeat.length
        : this.appService.app.humanAdmins.length;

    let minimumChanged = this.minimumSeatNumber !== previousMinimum;
    let wasAtPreviousMinimum = this.seatNumber === previousMinimum;

    if (
      (minimumChanged && wasAtPreviousMinimum) ||
      this.minimumSeatNumber > this.selectedSeatNumber
    ) {
      this.seatNumber = this.minimumSeatNumber;
      this.selectedSeatNumber = this.minimumSeatNumber;
    }
  }

  get renewalDate() {
    return moment().add(1, 'year');
  }

  get queryParamsForRedirect() {
    return {
      plan_id: this.selectedPlanId,
      billing_period: this.billingPeriod,
      seat_number: this.seatNumber,
      proactive_addon: this.selectedProactiveAddon,
      copilot_addon: this.selectedCopilotAddon,
      ai_insights_addon: this.hasSelectedPerformanceProAddon,
    };
  }

  get redirectUrl() {
    let baseUrl = window.location.origin;
    let url = this.router.urlFor('apps.app.teams-checkout', this.appService.app.id, {
      queryParams: this.queryParamsForRedirect,
    });
    return `${baseUrl}${url}`;
  }

  get adminIdsWithCopilotSeats() {
    return this.appService.app.humanAdminsWithCopilotSeat.map((admin: any) => admin.id);
  }

  get selectedPlanIds() {
    let planIds = [Number(this.selectedPlanId)];
    if (this.selectedProactiveAddon) {
      planIds.push(Number(PROACTIVE_SUPPORT_PLUS_BASE_ID));
    }
    if (this.selectedCopilotAddon) {
      planIds.push(Number(FIN_AI_COPILOT_BASE_ID));
    }
    if (this.hasSelectedPerformanceProAddon) {
      planIds.push(Number(PERFORMANCE_PRO_BASE_ID));
    }
    return planIds;
  }

  get copilotSeatNumber() {
    return this.copilotInitialised ? this.selectedCopilotAdmins.length : 0;
  }

  get selectedCopilotAddon() {
    return this.copilotSeatNumber > 0;
  }

  get sendCopilotAdminIds() {
    return this.copilotSeatNumber > 0;
  }

  get loading() {
    return this.quoteService.loading;
  }

  get quote() {
    let planIds = [Number(this.selectedPlanId), ...this.nonCorePlanIdsToFetch];
    return this.quoteService.getQuoteById(planIds);
  }

  get fullSeatPrice() {
    return this.quote?.fullSeatPrice(Number(this.selectedPlanId), Metric.core_seat_count) || 0;
  }

  get discountedSeatPrice() {
    return this.quote?.discountedAnnualSeatPrice(Number(this.selectedPlanId)) || 0;
  }

  get fullProactiveAddonPrice() {
    return this.quote?.fullBreakdownAmount(Number(PROACTIVE_SUPPORT_PLUS_BASE_ID)) || 0;
  }

  get discountedProactiveAddonPrice() {
    return this.quote?.fullBreakdownAmount(Number(PROACTIVE_SUPPORT_PLUS_BASE_ID)) || 0;
  }

  get perSeatCopilotAddonPrice() {
    return (
      this.quote?.fullSeatPrice(Number(FIN_AI_COPILOT_BASE_ID), Metric.copilot_seat_count) || 0
    );
  }

  get fullCopilotAddonPrice() {
    return this.perSeatCopilotAddonPrice * this.selectedCopilotAdmins.length;
  }

  get fullPerformanceProAddonPrice() {
    return this.quote?.fullBreakdownAmount(Number(PERFORMANCE_PRO_BASE_ID)) || 99;
  }

  get discountedCopilotSeatPrice() {
    return (
      this.quote?.discountedAnnualSeatPrice(
        Number(FIN_AI_COPILOT_BASE_ID),
        Metric.copilot_seat_count,
      ) || 0
    );
  }

  get monthlyCopilotPriceForSeats() {
    return this.discountedCopilotSeatPrice * this.copilotSeatNumber;
  }

  get monthlyDiscountedTotal() {
    let total = this.monthlyDiscountedSeatPrice;
    if (this.selectedProactiveAddon) {
      total += this.fullProactiveAddonPrice;
    }
    if (this.selectedCopilotAddon) {
      total += this.monthlyCopilotPriceForSeats;
    }
    if (this.hasSelectedPerformanceProAddon) {
      total += this.fullPerformanceProAddonPrice;
    }
    return total;
  }

  get totalPrice() {
    return this.monthlyDiscountedTotal * 12;
  }

  get totalPriceInCents() {
    return this.totalPrice * 100;
  }

  get totalPriceFormatted() {
    return this.intl.t('signup.teams.pricing5.annual-plans.basket.due-today-price', {
      yearly: true,
      price: this.intl.formatNumber(this.totalPrice, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }),
      htmlSafe: true,
    });
  }

  get monthlyFullSeatPrice() {
    return this.fullSeatPrice * this.seatNumber;
  }

  get monthlyDiscountedSeatPrice() {
    return this.discountedSeatUsagePrice;
  }

  get discountedSeatUsagePrice() {
    return this.discountedSeatPrice * this.seatNumber;
  }

  get monthlyTotal() {
    let total = this.monthlyFullSeatPrice;
    if (this.selectedProactiveAddon) {
      total += this.fullProactiveAddonPrice;
    }
    if (this.selectedCopilotAddon) {
      total += this.fullCopilotAddonPrice;
    }
    if (this.hasSelectedPerformanceProAddon) {
      total += this.fullPerformanceProAddonPrice;
    }
    return total;
  }

  get backButtonLabel() {
    return this.intl.t('signup.teams.header.back');
  }

  get corePlans() {
    return this.appService.app.allPlansOnPricingModel.filter(
      (plan: Plan) => !plan.product.addon && plan.selfServe,
    );
  }

  get nonCorePlanIdsToFetch() {
    return this.appService.app.allPlansOnPricingModel
      .filter((plan: Plan) => plan.product.addon && plan.selfServe)
      .map((plan: Plan) => plan.idAsNumber);
  }

  get planIdsForQuote() {
    return this.corePlans.map((plan: Plan) => [plan.idAsNumber, ...this.nonCorePlanIdsToFetch]);
  }

  get planIdsForInitialQuote() {
    return [Number(this.selectedPlanId), ...this.nonCorePlanIdsToFetch];
  }

  get showAnnualBillingSelector() {
    return this.appService.app.show5_1PricingModal;
  }

  get proactiveSupportBaseUsage() {
    return this.quote?.proactiveSupportBaseUsage;
  }

  suitableCorePlanExistsForPlanId(planId: number | undefined) {
    if (!planId) {
      return false;
    }
    return this.corePlans.find((plan: Plan) => plan.idAsNumber === planId);
  }

  backToOnboardingHomeAndOpenConversionModal() {
    let queryParams = { action: 'buy_intercom' };
    this.router.transitionTo(this.appService.app.onboardingHomeRoute, { queryParams });
  }

  @action
  onBackButtonClick() {
    if (this.showCreditCardPage) {
      this.setShowCreditCardPage();
    } else if (this.args.returnToLastPage && this.args.returnToLastPage === 'true') {
      window.history.back();
    } else {
      this.backToOnboardingHomeAndOpenConversionModal();
    }
  }

  @action showProactiveSupportProArticle() {
    window.Intercom('showArticle', this.appService.app.proactiveSupportProArticleId);
    this.sendAnalyticsEvent({
      action: 'clicked',
      object: 'metered_proactive_support_plus_article',
    });
  }

  @action
  setSelectedCopilotAdmins(selectedAdmins: string[]) {
    this.selectedCopilotAdmins = selectedAdmins;
  }

  @action
  setCopilotAsInitialised(value: boolean) {
    this.copilotInitialised = value;
  }

  @action
  setHasSelectedPerformanceProAddon(value: boolean) {
    this.hasSelectedPerformanceProAddon = value;
    this.sendAnalyticsEvent({
      action: value ? 'selected' : 'unselected',
      object: 'ai_insights_addon_selector',
    });
  }

  @action
  setSelectedProactiveAddon(value: boolean) {
    this.selectedProactiveAddon = value;
    this.sendAnalyticsEvent({
      action: value ? 'selected' : 'unselected',
      object: 'proactive_support_addon_selector',
    });
  }

  @action
  updateSelectedPlanId(plan: Plan) {
    this.selectedPlanId = plan.id;
    this.setSeatsToHumanAdminsOrCoreSeatLength();
    this.sendAnalyticsEvent({
      action: 'selected',
      object: 'plan_selector',
      context: { plan: this.selectedPlanId },
    });
  }

  @action
  updateSeatNumber(seatNumber: number, didRemoveTeammates?: boolean) {
    let previousSeatNumber = this.seatNumber;
    this.selectedSeatNumber = seatNumber;
    this.seatNumber = this.selectedSeatNumber;
    // This ensures seats are synced when the app updates after removal of a teammate
    if (didRemoveTeammates) {
      this.setSeatsToHumanAdminsOrCoreSeatLength();
      // Remove any copilot selections for teammates that were just removed,
      // but preserve the user's other in-memory copilot selections
      this.selectedCopilotAdmins = this.selectedCopilotAdmins.filter((adminId) =>
        this.appService.app.humanAdmins.some((admin: any) => admin.id === adminId),
      );
    }
    this.sendAnalyticsEvent({
      action: 'inputed',
      object: 'seat_number_input',
      context: {
        seat_number: `${this.seatNumber}`,
        previous_seat_number: `${previousSeatNumber}`,
        increased_seats: this.seatNumber > previousSeatNumber,
      },
    });
  }

  @action
  setShowCreditCardPage() {
    this.showCreditCardPage = !this.showCreditCardPage;
    if (this.showCreditCardPage) {
      this.sendAnalyticsEvent({
        action: 'clicked',
        object: 'continue_to_payment_button',
        context: {
          plans: this.selectedPlanIds,
          billing_period: `${this.billingPeriod}`,
          seat_number: `${this.seatNumber}`,
          proactive_addon: `${this.selectedProactiveAddon}`,
          ai_insights_addon: `${this.hasSelectedPerformanceProAddon}`,
        },
      });

      this.sendAnalyticsEvent({
        action: 'viewed',
        object: 'pricing_5_checkout',
      });
    }
  }

  @action sendAnalyticsEvent({
    action,
    object,
    context,
  }: {
    action: string;
    object: string;
    context?: Record<string, any>;
  }) {
    this.purchaseAnalyticsService.trackEvent({
      action,
      object,
      context,
      place: 'p5-checkout-monthly-to-annual',
    });
  }

  @keepLatestTask
  *getQuotes(): TaskGenerator<void> {
    let params = this.planIdsForQuote.map((planIds: string[]) => {
      return {
        ...DEFAULT_REQUEST_PARAMS,
        planIds,
        pricingModelIdentifier: PricingModelIdentifier.PRICING_5_1,
        source: 'pricing-five-monthly-to-annual-checkout',
        coreSeatCount: this.seatNumber,
      };
    });
    yield taskFor(this.quoteService.getQuotes).perform(params);
  }

  @keepLatestTask
  *convertSubscription(afterPaymentMethodSuccess: AfterPaymentMethodSuccess): TaskGenerator<void> {
    let { address, paymentMethod, stripeIntent, useExistingPaymentMethod, taxNumber } =
      afterPaymentMethodSuccess;
    let paramsForAnalytics = {
      seat_count: this.seatNumber,
      copilot_seat_count: this.copilotSeatNumber,
      plan_ids: this.selectedPlanIds,
      payment_intent: stripeIntent?.isPaymentIntent,
      use_existing_payment_method: useExistingPaymentMethod,
    };

    let params = {
      ...paramsForAnalytics,
      app_id: this.appService.app.id,
      ...(this.sendCopilotAdminIds && { copilot_admin_ids: this.selectedCopilotAdmins }),
      stripe_payment_method_id: paymentMethod,
      ...(address.streetAddress &&
        address.countryCode && {
          address: {
            country_code: address.countryCode,
            street_address: address.streetAddress,
            ...(address.stateCode && { state_code: address.stateCode }),
            ...(address.postCode && { postcode: address.postCode }),
            ...(address.city && { city: address.city }),
          },
        }),
      ...(stripeIntent?.isPaymentIntent && { stripe_payment_intent_id: stripeIntent.id }),
      ...(taxNumber && { tax_number: taxNumber }),
    };

    try {
      yield post(`/ember/conversions/convert_to_annual`, params);
      this.sendAnalyticsEvent({
        action: 'completed',
        object: 'monthly_to_annual',
        context: paramsForAnalytics,
      });
      this.notificationsService.notifyConfirmation(
        this.intl.t('signup.teams.pricing5.checkouts.monthly-to-annual.convert.success'),
      );
      this.router.transitionTo('apps.app.checkout.success', this.appService.app.id, {
        queryParams: {
          isBillingPeriodMigration: true,
        },
      });
    } catch (err) {
      console.error(err);
      if (err && err?.jqXHR?.responseJSON && err?.jqXHR?.responseJSON[0]) {
        this.notificationsService.notifyError(err.jqXHR.responseJSON[0]);
      } else {
        this.notificationsService.notifyError(
          this.intl.t('signup.teams.pricing5.annual-plans.stripe.generic-error'),
        );
      }
      throw err;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Signup::Teams::Pricing5::Checkouts::MonthlyToAnnualCheckout': typeof MonthlyToAnnualCheckout;
  }
}
